import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
} from "@mui/material";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";

/**
 * Shows a notification when the user contributes to an employee target.
 * @param {boolean} open
 * @param {Array<{ target_id, target_name, value, goal, current_total, remaining }>} contributions
 * @param {() => void} onClose
 */
export default function TargetProgressModal({ open, contributions = [], onClose }) {
  const navigate = useNavigate();
  const first = contributions[0];

  if (!first) return null;

  const metricLabel = (first.target_name || "").toLowerCase().includes("conversion")
    ? "conversion"
    : (first.target_name || "").toLowerCase().includes("revenue")
      ? "revenue"
      : "contribution";
  const valueText = first.value === 1 && metricLabel === "conversion"
    ? "+1 conversion"
    : metricLabel === "revenue"
      ? `+${Number(first.value).toLocaleString()} revenue`
      : `+${first.value} ${metricLabel}`;
  const remaining = first.remaining;
  const remainingText =
    remaining > 0
      ? `Only ${remaining} more ${metricLabel === "conversion" ? "conversions" : metricLabel === "revenue" ? "in revenue" : "to go"} to reach your goal!`
      : "You've reached your goal!";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogContent sx={{ textAlign: "center", py: 4 }}>
        <Box sx={{ color: "primary.main", mb: 2 }}>
          <TrackChangesIcon sx={{ fontSize: 56 }} />
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          🎉 Target Progress!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          You just added {valueText} to your {first.target_name}.
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
          Progress: {Number(first.current_total).toLocaleString()} / {Number(first.goal).toLocaleString()}
        </Typography>
        <Typography variant="body2" color="primary.main" fontWeight={600}>
          {remainingText}
        </Typography>
        <Box sx={{ mt: 3, display: "flex", gap: 1, justifyContent: "center" }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
          <Button variant="contained" onClick={() => { onClose(); navigate(`/targets/${first.target_id}`); }}>
            View Target
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
